import { useState } from 'react';
import type { GuestDuplicateGroup } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { useMergeEventGuests } from '../../hooks/useEventGuests';
import { useDialog } from '../../context/DialogContext';
import { extractApiErrorMessage } from '../../utils/apiError';
import { formatPhone } from '@peles/shared';

export interface GuestDuplicatesModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  groups: GuestDuplicateGroup[];
}

const REASONS: Record<string, string> = {
  email: 'the same email',
  phone: 'the same phone',
  name: 'the same name',
};

function reasonText(reasons: string[]): string {
  const words = reasons.map((r) => REASONS[r] ?? r);
  return words.length > 1
    ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
    : (words[0] ?? '');
}

/**
 * Guests that look like one party entered twice, grouped, with why. Choose
 * the one to keep and merge the rest into it: it keeps what it has and takes
 * what it lacks — contacts, party members, answers, invitations, seat — from
 * the others, which are then removed. "Keep separate" hides a group for now.
 */
export function GuestDuplicatesModal({
  open,
  onClose,
  eventId,
  groups,
}: GuestDuplicatesModalProps) {
  const merge = useMergeEventGuests(eventId);
  const { confirm, alert } = useDialog();
  const [keep, setKeep] = useState<Record<string, string>>({});
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const shown = groups
    .map((group) => ({ group, key: group.guests.map((g) => g.id).join(':') }))
    .filter(({ key }) => !hidden.has(key));

  const mergeGroup = async (group: GuestDuplicateGroup, keepId: string) => {
    const kept = group.guests.find((g) => g.id === keepId);
    const others = group.guests.filter((g) => g.id !== keepId);
    const ok = await confirm({
      title: 'Merge guests',
      message: `Keep ${kept?.name} and merge ${others.map((g) => g.name).join(', ')} into them? The merged guests are removed.`,
      confirmLabel: 'Merge',
      variant: 'warning',
    });
    if (!ok) return;
    try {
      await merge.mutateAsync({ keepId, mergeIds: others.map((g) => g.id) });
    } catch (err) {
      await alert({
        title: 'Could not merge',
        message: extractApiErrorMessage(err, 'Nothing was merged.'),
        variant: 'error',
      });
    }
  };

  return (
    <EntityModal open={open} onClose={onClose} title="Possible duplicates" size="md">
      {shown.length === 0 ? (
        <p className="text-sm text-gray-600">No possible duplicates left to look at.</p>
      ) : (
        <ul className="space-y-4">
          {shown.map(({ group, key }) => {
            const keepId = keep[key] ?? group.guests[0]?.id;
            return (
              <li key={key} className="rounded-2xl border border-gray-200 p-4">
                <p className="text-sm text-gray-600 mb-3">
                  These share {reasonText(group.reasons)}. Keep:
                </p>
                <div role="radiogroup" aria-label="Guest to keep" className="space-y-2">
                  {group.guests.map((guest) => (
                    <label
                      key={guest.id}
                      className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer ${
                        keepId === guest.id ? 'border-primary-300 bg-primary-50' : 'border-gray-200'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`keep-${key}`}
                        checked={keepId === guest.id}
                        onChange={() => setKeep((k) => ({ ...k, [key]: guest.id }))}
                        className="mt-1"
                      />
                      <span className="text-sm">
                        <span className="font-medium text-gray-900">{guest.name}</span>
                        <span className="block text-xs text-gray-500">
                          {[
                            guest.email,
                            formatPhone(guest.phone),
                            guest.familyGroup,
                            `${guest.partySize} ${guest.partySize === 1 ? 'person' : 'people'}`,
                            guest.tableNumber != null ? `Table ${guest.tableNumber}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setHidden((h) => new Set(h).add(key))}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Keep separate
                  </button>
                  <button
                    type="button"
                    disabled={merge.isPending || !keepId}
                    onClick={() => void mergeGroup(group, keepId)}
                    className="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm hover:bg-primary-700 disabled:opacity-50"
                  >
                    Merge into the one kept
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </EntityModal>
  );
}
